/**
 * AWB Wallpaper Calculator — standalone custom element.
 * Scoped entirely to `this`, safe to use more than once on a page.
 *
 * External API:
 *   calculatorEl.setRollQuantity(n) — call when the variant picker changes,
 *                                       since roll_quantity is per-variant
 *                                       (e.g. 2-roll vs 4-roll set options).
 *
 * Strip-length math (AWB's own formula, not reverse-engineered from any
 * external tool — see AWB-Design-System-Reference.md for derivation and
 * test data):
 *
 *   Repeat patterns cycle through n vertical drop positions:
 *     - random match (no repeat value): n=1, no repeat rounding at all,
 *       every strip length = trimmed height (wall height + trim allowance).
 *     - straight match: n=1, every strip length = ceil(trimmedHeight/repeat) * repeat.
 *     - half-drop: n=2. Position 0 (short) needs ceil(trimmedHeight/repeat) repeats.
 *       Position 1 (long) needs one additional full repeat.
 *     - quarter-drop: n=4. Positions need ceil(trimmedHeight/repeat)+0, +1, +2, +3
 *       repeats respectively. (Direct generalization of the half-drop rule;
 *       unverified against real quarter-drop product data as of Aug 2026 —
 *       confirm against a real quarter-drop product before trusting fully.)
 *
 *   1. Total width = sum of all wall widths
 *   2. Strips needed = ceil(total width / roll width)
 *   3. A fixed trim allowance (2in / 5cm, matching the hanging instructions'
 *      guidance to leave extra length at the top of each drop) is added to
 *      the wall height before any repeat math, for every strip, always —
 *      not optional, not tied to the "Additional Drops" checkbox.
 *   4. For each of the n positions: length = (ceil(trimmedHeight/repeat) + i) * repeat
 *      (random match skips this and uses length = trimmedHeight for every strip)
 *   5. Strips needed are distributed evenly across the n positions
 *      (floor(stripsNeeded/n) each), with any remainder handed to the
 *      shortest positions first (lowest i), so short strips always
 *      outnumber or equal long strips, never the reverse.
 *   6. Total linear length = sum of (count at position × length at position)
 *      across all positions. If the "Include additional drop(s) of
 *      wallpaper" checkbox is checked, N buffer strips (N = the quantity
 *      field, min 1) are added at the longest position's length (random
 *      match has only one position, so its buffer strips are just the
 *      trimmed height). Unchecked (0 buffer) by default.
 *   7. Rolls = ceil(total linear length / roll length).
 */
class AwbWallpaperCalculator extends HTMLElement {
  #wallCount = 1;
  #maxWalls = 8;
  #unitMode = 'imperial'; // 'imperial' | 'metric'
  #rollQuantity = 1;
  #controller = new AbortController();
  #defaultRepeatValue = null;
  #trimAllowanceM = 0.05; // fixed 5cm (labeled 2in in customer-facing copy), added to every strip's height

  connectedCallback() {
    const { signal } = this.#controller;

    this.wallsList = this.querySelector('[data-walls-list]');
    this.addWallBtn = this.querySelector('[data-calc-add-wall]');
    this.unitSelect = this.querySelector('[data-calc-unit]');
    this.repeatInput = this.querySelector('[data-repeat-val]');
    this.repeatTypeDisplay = this.querySelector('[data-repeat-type-display]');
    this.runBtn = this.querySelector('[data-calc-run]');
    this.resetBtn = this.querySelector('[data-calc-reset]');
    this.headline = this.querySelector('[data-calc-headline]');
    this.bufferCheckbox = this.querySelector('[data-calc-buffer]');
    this.bufferQtyInput = this.querySelector('[data-calc-buffer-qty]');

    this.wallsList.appendChild(this.#renderWall(1));

    this.addWallBtn?.addEventListener('click', () => {
      if (this.#wallCount >= this.#maxWalls) return;
      this.#wallCount++;
      this.wallsList.appendChild(this.#renderWall(this.#wallCount));
      if (this.#wallCount >= this.#maxWalls) this.addWallBtn.style.display = 'none';
    }, { signal });

    // Pre-fill repeat from data attributes (sourced from metafields by the snippet)
    const repeatAttr = this.dataset.repeat || '';
    const repeatMatch = repeatAttr.match(/[\d.]+/);
    if (this.repeatInput && repeatMatch) {
      this.repeatInput.value = parseFloat(repeatMatch[0]);
    }
    this.#defaultRepeatValue = repeatMatch ? parseFloat(repeatMatch[0]) : null;

    const repeatTypeVal = (this.dataset.repeatType || '').trim();
    if (this.repeatTypeDisplay) {
      this.repeatTypeDisplay.value = repeatTypeVal
        ? repeatTypeVal.charAt(0).toUpperCase() + repeatTypeVal.slice(1)
        : '—';
      this.repeatTypeDisplay.style.color = repeatTypeVal ? 'var(--ink)' : 'var(--muted)';
    }

    this.unitSelect?.addEventListener('change', (e) => this.#handleUnitChange(e), { signal });
    this.runBtn?.addEventListener('click', () => this.#calculate(), { signal });
    this.resetBtn?.addEventListener('click', () => this.#reset(), { signal });

    this.#rollQuantity = parseInt(this.dataset.rollQuantity, 10) || 1;
    this.#updateHeadline();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * External API — call when the variant picker changes, since roll_quantity
   * is per-variant (e.g. 2-roll vs 4-roll set options on the same product).
   * @param {number} n
   */
  setRollQuantity(n) {
    this.#rollQuantity = parseInt(n, 10) || 1;
    this.#updateHeadline();
  }

  #updateHeadline() {
    if (!this.headline) return;
    this.headline.textContent = this.#rollQuantity > 1 ? 'How many sets do I need?' : 'How many rolls do I need?';
  }

  #ordinal(n) {
    if (n === 1) return '1st';
    if (n === 2) return '2nd';
    if (n === 3) return '3rd';
    return n + 'th';
  }

  #numberWord(n) {
    const words = { 1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR' };
    return words[n] || n;
  }

  #toM(major, minor) {
    if (this.#unitMode === 'metric') return major + minor / 100; // m + cm
    return (major + minor / 12) * 0.3048; // ft + in
  }

  #repeatValToM() {
    const v = parseFloat(this.repeatInput?.value) || 0;
    return this.#unitMode === 'metric' ? v / 100 : v * 0.0254;
  }

  /** Buffer quantity — 0 unless the checkbox is checked, then the quantity field's value (min 1). */
  #bufferQty() {
    if (!this.bufferCheckbox?.checked) return 0;
    const v = parseInt(this.bufferQtyInput?.value, 10);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  /**
   * Number of drop positions (n) for a given repeat type string.
   * n=1 covers straight match (single uniform strip length).
   * Random match (repeatM<=0) is handled separately upstream and never
   * calls this — it skips repeat math entirely.
   */
  #dropPositions(repeatType) {
    const t = (repeatType || '').toLowerCase();
    if (t.indexOf('quarter') >= 0) return 4;
    if (t.indexOf('half') >= 0 || t.indexOf('drop') >= 0) return 2;
    return 1;
  }

  #renderWall(n) {
    const d = document.createElement('div');
    d.className = 'awb-calc-wall-entry awb-calc-section';
    d.dataset.wall = n;
    const mjLabel = this.#unitMode === 'metric' ? 'M' : 'FT';
    const mnLabel = this.#unitMode === 'metric' ? 'CM' : 'IN';
    d.innerHTML = `
      <label class="awb-calc-label">Length of ${this.#ordinal(n)} Wall</label>
      <div class="awb-calc-pair">
        <div class="awb-calc-field">
          <input class="awb-calc-input awb-wall-major" type="number" min="0" step="1" value="0">
          <span class="awb-calc-unit-label awb-wall-unit-major">${mjLabel}</span>
        </div>
        <div class="awb-calc-field">
          <input class="awb-calc-input awb-wall-minor" type="number" min="0" step="1" value="0">
          <span class="awb-calc-unit-label awb-wall-unit-minor">${mnLabel}</span>
        </div>
      </div>
    `;
    return d;
  }

  #handleUnitChange(e) {
    const prevMode = this.#unitMode;
    this.#unitMode = e.target.value;
    const mjLabel = this.#unitMode === 'metric' ? 'M' : 'FT';
    const mnLabel = this.#unitMode === 'metric' ? 'CM' : 'IN';
    const rpLabel = this.#unitMode === 'metric' ? 'CM' : 'IN';

    this.querySelector('[data-h-unit-major]').textContent = mjLabel;
    this.querySelector('[data-h-unit-minor]').textContent = mnLabel;
    this.querySelector('[data-repeat-unit-label]').textContent = rpLabel;
    this.querySelectorAll('.awb-wall-unit-major').forEach((el) => { el.textContent = mjLabel; });
    this.querySelectorAll('.awb-wall-unit-minor').forEach((el) => { el.textContent = mnLabel; });

    if (this.repeatInput?.value) {
      const v = parseFloat(this.repeatInput.value) || 0;
      if (prevMode === 'imperial' && this.#unitMode === 'metric') {
        this.repeatInput.value = Math.round(v * 2.54 * 10) / 10; // in → cm
      } else if (prevMode === 'metric' && this.#unitMode === 'imperial') {
        this.repeatInput.value = Math.round(v / 2.54 * 10) / 10; // cm → in
      }
    }
  }

  #calculate() {
    const resultLine = this.querySelector('[data-calc-result-line]');

    const hMaj = parseFloat(this.querySelector('[data-h-major]').value) || 0;
    const hMin = parseFloat(this.querySelector('[data-h-minor]').value) || 0;
    const hM = this.#toM(hMaj, hMin);

    if (hM <= 0) {
      resultLine.innerHTML = 'We estimate you will require <strong>?</strong> — please enter wall height';
      resultLine.classList.add('visible');
      return;
    }

    let totalWidthM = 0;
    this.querySelectorAll('.awb-calc-wall-entry').forEach((entry) => {
      const maj = parseFloat(entry.querySelector('.awb-wall-major').value) || 0;
      const min = parseFloat(entry.querySelector('.awb-wall-minor').value) || 0;
      totalWidthM += this.#toM(maj, min);
    });

    // Roll dimensions must come from the product's metafields (data-roll-width /
    // data-roll-length, set by the snippet). No fallback is applied here — if
    // these are blank, the calculation is refused rather than silently using
    // a generic size.
    const rwAttr = this.dataset.rollWidth || '';
    const rlAttr = this.dataset.rollLength || '';
    const rwMatch = rwAttr.match(/[\d.]+/);
    const rlMatch = rlAttr.match(/[\d.]+/);

    if (!rwMatch || !rlMatch) {
      resultLine.innerHTML = 'We estimate you will require <strong>?</strong> — roll dimensions missing for this product';
      resultLine.classList.add('visible');
      return;
    }

    const rollWidthM = parseFloat(rwMatch[0]) * 0.0254;   // stored in inches
    const rollLengthM = parseFloat(rlMatch[0]) * 0.3048;  // stored in feet

    const repeatM = this.#repeatValToM();
    const repeatType = this.dataset.repeatType || '';

    // Fixed trim allowance added to every strip, always — see class field comment.
    const trimmedHeightM = hM + this.#trimAllowanceM;

    const stripsNeeded = Math.ceil(totalWidthM / rollWidthM);
    const bufferQty = this.#bufferQty();

    let totalLengthM;

    if (repeatM <= 0) {
      // Random match — no repeat to align, every strip is just the trimmed height.
      totalLengthM = (stripsNeeded + bufferQty) * trimmedHeightM;
    } else {
      const n = this.#dropPositions(repeatType);
      const shortRepeats = Math.ceil(trimmedHeightM / repeatM);

      // Position i needs (shortRepeats + i) full repeats. i=0 is the
      // shortest (straight match's only position; half-drop's short strip);
      // higher i positions need progressively more repeat coverage.
      const lengths = [];
      for (let i = 0; i < n; i++) {
        lengths.push((shortRepeats + i) * repeatM);
      }

      // Distribute strips evenly across positions; remainder goes to the
      // shortest (lowest-index) positions first, so short strips always
      // outnumber or equal long strips.
      const base = Math.floor(stripsNeeded / n);
      const remainder = stripsNeeded % n;
      const counts = new Array(n).fill(base);
      for (let r = 0; r < remainder; r++) {
        counts[r] += 1;
      }

      totalLengthM = 0;
      for (let i = 0; i < n; i++) {
        totalLengthM += counts[i] * lengths[i];
      }

      // bufferQty extra strips at the longest position's length.
      totalLengthM += lengths[n - 1] * bufferQty;
    }

    const rolls = Math.max(1, Math.ceil(totalLengthM / rollLengthM));

    if (this.#rollQuantity === 4) {
      // Round up to nearest even number so it can be expressed as 4s and 2s,
      // then use as many 4-roll units as possible and one 2-roll unit for
      // the remainder — gets closer to the real number needed than always
      // rounding up to the next multiple of 4.
      const total = rolls % 2 === 0 ? rolls : rolls + 1;
      const fours = Math.floor(total / 4);
      const twos = (total % 4) / 2;

      const parts = [];
      if (fours > 0) parts.push(`<strong>${fours}x</strong> ${this.#numberWord(4)} ROLL ${fours === 1 ? 'SET' : 'SETS'}`);
      if (twos > 0) parts.push(`<strong>${twos}x</strong> ${this.#numberWord(2)} ROLL SET`);

      resultLine.innerHTML = `We estimate you will require <strong>${total}</strong> rolls<p>${parts.join(' and ')}</p>`;
    } else if (this.#rollQuantity > 1) {
      const sets = Math.ceil(rolls / this.#rollQuantity);
      const total = sets * this.#rollQuantity;
      const word = this.#numberWord(this.#rollQuantity);

      resultLine.innerHTML = `We estimate you will require <strong>${total}</strong> rolls<p><strong>${sets}x</strong> ${word} ROLL ${sets === 1 ? 'SET' : 'SETS'}</p>`;
    } else {
      resultLine.innerHTML = `We estimate you will require <strong>${rolls}</strong> ${rolls === 1 ? 'roll' : 'rolls'}`;
    }
    resultLine.classList.add('visible');
  }

  #reset() {
    this.querySelector('[data-h-major]').value = 0;
    this.querySelector('[data-h-minor]').value = 0;

    this.wallsList.innerHTML = '';
    this.#wallCount = 1;
    this.wallsList.appendChild(this.#renderWall(1));
    this.addWallBtn.style.display = '';

    if (this.repeatInput && this.#defaultRepeatValue !== null) {
      this.repeatInput.value = this.#defaultRepeatValue;
    }

    if (this.bufferCheckbox) this.bufferCheckbox.checked = false;
    if (this.bufferQtyInput) this.bufferQtyInput.value = 1;

    this.querySelector('[data-calc-result-line]').classList.remove('visible');
  }
}

if (!customElements.get('awb-wallpaper-calculator')) {
  customElements.define('awb-wallpaper-calculator', AwbWallpaperCalculator);
}